import React, { useEffect } from 'react';

import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import RecentSection from 'client/app/apps/landing-page/RecentSection';
import TutorialsSection from 'client/app/apps/landing-page/TutorialsSection';
import { NavigationPanel, useCreateNew } from 'client/app/components/NavigationSidepanel';
import { experimentsRoutes } from 'client/app/lib/nav/actions';
import setBrowserTabTitle from 'client/app/lib/setBrowserTabTitle';
import Colors from 'common/ui/Colors';
import Button from 'common/ui/components/Button';

export default function LandingPageSceen() {
  const { createNew, templateWorklowsDialog } = useCreateNew();
  useEffect(() => setBrowserTabTitle('Home'), []);

  return (
    <Wrapper>
      <Navigation />
      <Content>
        <Section>
          <SectionTitle>Start</SectionTitle>
          <GettingStarted>
            <DesignANewWorkflowButton onClick={() => createNew('new-workflow-builder')}>
              <ButtonTitle>Design a new workflow</ButtonTitle>
              <ButtonDescription>
                Create a new workflow or adapt an example workflow to your needs
              </ButtonDescription>
            </DesignANewWorkflowButton>
            <BrowseTemplatesButton onClick={() => createNew('new-protocol')}>
              <ButtonTitle>Browse protocols</ButtonTitle>
              <ButtonDescription>
                Customize and run pre-defined protocol
              </ButtonDescription>
            </BrowseTemplatesButton>
            <NewExperimentButton onClick={() => createNew('new-experiment')}>
              <ButtonTitle>New experiment</ButtonTitle>
              <ButtonDescription>
                Organize and visualize the entire experimental journey
              </ButtonDescription>
            </NewExperimentButton>
          </GettingStarted>
        </Section>
        <Section>
          <SectionTitle>Recent</SectionTitle>
          <RecentSection />
        </Section>
        <section>
          <SectionTitle>Learn</SectionTitle>
          <TutorialsSection />
          <LearnMore>
            <Link to={experimentsRoutes.tutorials.getPath()}>
              <ViewMoreButton variant="secondary" color="primary">
                View more
              </ViewMoreButton>
            </Link>
          </LearnMore>
        </section>
      </Content>
      {templateWorklowsDialog}
    </Wrapper>
  );
}

const Wrapper = styled('div')({
  position: 'relative',
  display: 'grid',
  gridTemplate: 'minmax(0, 1fr) / auto 1fr',
  backgroundImage: 'url(/app/assets/images/landing/footer-bg.png)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center bottom',
  backgroundSize: '3000px 412px',
  backgroundAttachment: 'local',
  overflow: 'auto',
  flex: 1,
});

const Content = styled('div')(({ theme }) => ({
  padding: theme.spacing(8, 0),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(9),
  height: 'fit-content',
}));

const GettingStarted = styled('div')(({ theme: { spacing } }) => ({
  display: 'grid',
  gridTemplate: `
    'w ne' auto
    'w se' auto / 2fr 1fr`,
  gap: spacing(6),
}));

const ButtonTitle = styled('span')({
  fontFamily: 'Inter',
  fontSize: 14,
  fontWeight: 900,
});

const ButtonDescription = styled('span')({
  fontFamily: 'Inter',
  fontSize: 13,
  fontWeight: 300,
});

const StartButton = styled('button')(({ theme: { spacing } }) => ({
  border: '0',
  borderRadius: spacing(3),
  display: 'grid',
  gridTemplate: `
    'action' auto
    'explanation' auto / auto
  `,
  justifyContent: 'start',
  alignContent: 'end',
  justifyItems: 'start',
  alignItems: 'start',
  backgroundPosition: 'top left',
  backgroundSize: '100% 100%',
  backgroundRepeat: 'no-repeat',
  color: Colors.TEXT_PRIMARY,
  padding: spacing(6),
  gap: spacing(3),
  textAlign: 'left',
  cursor: 'pointer',
  outline: `1px solid rgba(0,0,0,.2)`,
  outlineOffset: -1,

  '&:hover,&:focus': {
    color: Colors.PRIMARY_MAIN,
    outlineColor: Colors.PRIMARY_MAIN,
  },
}));

const DesignANewWorkflowButton = styled(StartButton)(({ theme: { spacing } }) => ({
  gridArea: 'w',
  gap: spacing(4),
  backgroundImage: 'url(/app/assets/images/landing/design-a-new-workflow-bg.png)',

  [`${ButtonTitle}`]: {
    fontSize: 22,
  },

  [`${ButtonDescription}`]: {
    fontSize: 15,
    maxWidth: 250,
  },
}));

const BrowseTemplatesButton = styled(StartButton)({
  backgroundImage: 'url(/app/assets/images/landing/browse-templates-bg.png)',
  gridArea: 'ne',

  [`${ButtonDescription}`]: {
    maxWidth: 200,
  },
});

const NewExperimentButton = styled(StartButton)({
  backgroundImage: 'url(/app/assets/images/landing/new-experiment-bg.png)',
  gridArea: 'se',

  [`${ButtonDescription}`]: {
    maxWidth: 200,
  },
});

const SectionTitle = styled('h1')(({ theme }) => ({
  margin: `0 0 ${theme.spacing(7)} 0`,
  fontSize: 32,
  fontWeight: 900,
  fontFamily: 'Inter',
  lineHeight: '32px',
  '::after': {
    content: '"."',
    color: Colors.PRIMARY_MAIN,
  },
}));

const Section = styled('section')(({ theme }) => ({
  width: theme.breakpoints.values.md,
  marginRight: theme.spacing(8),
}));

const LearnMore = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(5),
  display: 'grid',
  placeItems: 'center',
}));

const Navigation = styled(NavigationPanel)(({ theme }) => ({
  position: 'sticky',
  top: 40,
  margin: theme.spacing(8),
  zIndex: 2,
}));

const ViewMoreButton = styled(Button)({
  background: `rgba(255,255,255,.4)`,
  borderColor: Colors.WHITE,
  color: Colors.TEXT_PRIMARY,
  '&:hover': {
    background: Colors.WHITE,
    borderColor: Colors.WHITE,
    color: Colors.PRIMARY_MAIN,
  },
});
